import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";

export default function ReadOnlyCalendar({
  availability,
  show,
  setShow,
  setAvailabilityForm,
}) {
  const [readOnlyDate, setReadOnlyDate] = useState(new Date());

  const readOnlyWeekDates = getWeekDates(readOnlyDate);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const handleReadOnlyPrevWeek = () => {
    const newDate = new Date(readOnlyDate);
    newDate.setDate(readOnlyDate.getDate() - 7);
    setReadOnlyDate(newDate);
  };

  const handleReadOnlyNextWeek = () => {
    const newDate = new Date(readOnlyDate);
    newDate.setDate(readOnlyDate.getDate() + 7);
    setReadOnlyDate(newDate);
  };

  return (
    <Card className="w-full max-w-6xl mx-auto bg-white">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b">
        <CardTitle className="text-xl font-semibold text-gray-700">
          Call Availability (CET)
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReadOnlyPrevWeek}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-gray-600"
            onClick={() => setShow(!show)}
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReadOnlyNextWeek}
            className="text-gray-600"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <i
            className="fa-times fa-solid"
            onClick={() => setAvailabilityForm(false)}
          ></i>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-8 border-b">
          <div className="p-2 border-r"></div>
          {readOnlyWeekDates.map((date, index) => (
            <div
              key={index}
              className="p-2 text-center border-r last:border-r-0"
            >
              <div className="text-sm font-medium text-gray-500">
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div className="text-lg font-semibold text-gray-700">
                {date.getDate()}
              </div>
            </div>
          ))}
        </div>
        <ScrollArea className="h-[600px]">
          <div className="grid grid-cols-8">
            {hours.map((hour) => (
              <React.Fragment key={hour}>
                <div className="p-2 text-right pr-4 border-r text-sm text-gray-500">
                  {formatTime(hour)}
                </div>
                {readOnlyWeekDates.map((date, dateIndex) => {
                  const isWeekendDay = isWeekend(date);
                  const isSelected = isSlotSelectedCET(
                    date,
                    hour,
                    availability
                  );
                  return (
                    <div
                      key={`${hour}-${dateIndex}`}
                      className={`border-r border-b last:border-r-0 ${
                        isWeekendDay ? "bg-gray-100" : ""
                      }`}
                    >
                      {!isWeekendDay && isSelected && (
                        <div className="w-full h-full bg-blue-200"></div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

const getWeekDates = (date) => {
  const week = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(date);
    day.setDate(date.getDate() - date.getDay() + i);
    week.push(day);
  }
  return week;
};

const formatTime = (hour) => {
  return `${hour.toString().padStart(2, "0")}:00`;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isSlotSelectedCET = (date, hour, availability) => {
  // Convert CET hour to UTC
  const utcHour = (hour - 2 + 24) % 24;
  // Create Unix timestamp for UTC time
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), utcHour, 0, 0)
  );
  const timestamp = Math.floor(utcDate.getTime() / 1000);
  return availability.includes(timestamp);
};
